/**
 * AudioManager — Spellroads audio system.
 * Wraps Howler.js with game-specific sound event routing,
 * volume controls, and graceful degradation when audio files
 * are missing (expected during development before real assets exist).
 */

import { Howl, Howler } from 'howler';
import DebugLog from '../debug/DebugLog.js';

// Tips shown on loading screen (one chosen at random each boot)
export const LOADING_TIPS = [
  'Your party earns Fame by completing quests. Higher fame unlocks better companions and trade prices.',
  'Camping lets you forage, assign guard duty, and cook meals that grant next-day bonuses.',
  'Status effects can combo: set an enemy on fire after coating them in Oil for a devastating Explosion.',
  'The Navigator decides which direction the party moves on the world map. Change it in the party panel.',
  'Skill trees are shared across archetypes. A warrior can invest in Restoration — it just costs more.',
  'Berserkers deal more damage at low HP. Keep one in your party as a finisher.',
  'Scouts reduce the chance of ambush and reveal encounter types before you enter them.',
  'Rations matter. A hungry party rests poorly. A party with a Cook rests better.',
  'Local maps are procedurally generated. Each region visit is unique.',
  'Named companions have personal questlines. Completing them unlocks their second skill tree.',
  'Dvergr Runecasters can inscribe temporary runes onto allies\' weapons mid-combat.',
  'Town prices are affected by your party\u2019s Fame tier — respected parties get better deals.',
  'Kobolds are weak alone but terrifying in large groups. Prioritize their Warchief.',
  'The Witch-finder grants party-wide bonuses against undead. Worth hiring before entering ruins.',
  'You can assign any character as the party Healer regardless of class. Wisdom governs healing.',
];

// Category → volume multiplier (0.0 – 1.0)
const CATEGORY_VOLUMES = {
  sfx:   1.0,
  music: 0.6,
  ui:    0.8,
  voice: 0.9,
};

const AudioManager = {
  /** @type {Map<string, Howl>} Loaded sound cache */
  _sounds: new Map(),

  /** @type {Howl|null} Currently playing music track */
  _currentMusic: null,

  /** @type {string|null} ID of current music track */
  _currentMusicId: null,

  /** @type {object} sound_map.json contents */
  _soundMap: null,

  /** @type {boolean} Whether init completed successfully */
  _ready: false,

  /** @type {number} Master volume 0.0 – 1.0 */
  _masterVolume: 0.7,

  /** @type {boolean} Whether audio is muted */
  _muted: false,

  /**
   * Initialize AudioManager: load sound_map.json.
   * Does NOT preload audio files — they load on first play.
   */
  async init() {
    try {
      const mod = await import('../data/sound_map.json', { assert: { type: 'json' } });
      this._soundMap = mod.default ?? mod;
    } catch (err) {
      this._soundMap = {};
      DebugLog.warn(`[audio] Failed to load sound_map.json: ${err.message}`);
    }

    Howler.volume(this._masterVolume);
    this._ready = true;
    DebugLog.info(`[audio] AudioManager ready — ${Object.keys(this._soundMap).length} sound events mapped`);
  },

  /**
   * Play a sound effect by event name (from sound_map.json).
   * Silently no-ops if the file doesn't exist (dev environment without assets).
   * @param {string} eventName — e.g. 'attack_hit', 'heal', 'level_up'
   * @param {object} [opts] — { volume, rate, loop }
   */
  play(eventName, opts = {}) {
    if (!this._ready || this._muted) return;

    const filePath = this._soundMap?.[eventName];
    if (!filePath) return; // Not mapped — silent

    const cacheKey = filePath;
    let howl = this._sounds.get(cacheKey);

    if (!howl) {
      howl = new Howl({
        src: [filePath],
        volume: (opts.volume ?? 1.0) * CATEGORY_VOLUMES.sfx,
        rate: opts.rate ?? 1.0,
        loop: opts.loop ?? false,
        onloaderror: () => {
          // File not found — remove from cache so we don't retry noisily
          this._sounds.delete(cacheKey);
        },
      });
      this._sounds.set(cacheKey, howl);
    }

    howl.play();
    DebugLog.event(`[audio] play; event=${eventName} file=${filePath}`);
  },

  /**
   * Play background music, crossfading from the current track.
   * @param {string} trackId — e.g. 'music_world', 'music_combat', 'music_town'
   * @param {string} src — URL/path to audio file
   * @param {object} [opts] — { volume, fadeMs }
   */
  playMusic(trackId, src, opts = {}) {
    if (!this._ready) return;

    const fadeMs = opts.fadeMs ?? 1500;

    if (this._currentMusicId === trackId) return; // Already playing this track

    // Fade out current music
    if (this._currentMusic) {
      const outgoing = this._currentMusic;
      outgoing.fade(outgoing.volume(), 0, fadeMs);
      setTimeout(() => { outgoing.stop(); outgoing.unload(); }, fadeMs + 100);
    }

    const vol = (opts.volume ?? 1.0) * CATEGORY_VOLUMES.music * this._masterVolume;
    const music = new Howl({
      src: [src],
      volume: 0,
      loop: true,
      onloaderror: () => {
        DebugLog.warn(`[audio] Music file not found: ${src}`);
        if (this._currentMusic === music) {
          this._currentMusic = null;
          this._currentMusicId = null;
        }
      },
    });

    music.play();
    music.fade(0, vol, fadeMs);

    this._currentMusic = music;
    this._currentMusicId = trackId;
    DebugLog.event(`[audio] music; track=${trackId}`);
  },

  /** Stop all music immediately (or with optional fade). */
  stopMusic(fadeMs = 800) {
    if (!this._currentMusic) return;
    const outgoing = this._currentMusic;
    outgoing.fade(outgoing.volume(), 0, fadeMs);
    setTimeout(() => { outgoing.stop(); outgoing.unload(); }, fadeMs + 100);
    this._currentMusic = null;
    this._currentMusicId = null;
  },

  /** Set master volume (0.0 – 1.0) and persist to localStorage. */
  setVolume(v) {
    this._masterVolume = Math.max(0, Math.min(1, v));
    Howler.volume(this._masterVolume);
    try { localStorage.setItem('sr_volume', String(this._masterVolume)); } catch (_) {}
    DebugLog.info(`[audio] volume set to ${this._masterVolume}`);
  },

  getVolume() { return this._masterVolume; },

  /** Mute/unmute all audio. */
  setMuted(muted) {
    this._muted = muted;
    Howler.mute(muted);
    try { localStorage.setItem('sr_muted', String(muted)); } catch (_) {}
  },

  isMuted() { return this._muted; },

  /** Restore saved volume/mute preferences from localStorage. */
  restorePreferences() {
    try {
      const vol = parseFloat(localStorage.getItem('sr_volume') ?? '');
      if (!isNaN(vol)) this.setVolume(vol);
      const muted = localStorage.getItem('sr_muted') === 'true';
      if (muted) this.setMuted(true);
    } catch (_) {}
  },

  /** Return a random loading tip. */
  getRandomTip() {
    return LOADING_TIPS[Math.floor(Math.random() * LOADING_TIPS.length)];
  },
};

export default AudioManager;
